import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useGameStore } from "../store";
import { Users, Copy, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export function RoomCreator() {
  const { createRoom, players, roomCode, loading, setPlayerName } =
    useGameStore();
  const [name, setName] = useState("");
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const handleCreateRoom = async () => {
    if (name.trim()) {
      setPlayerName(name);
      await createRoom();
      setShowQR(true);
    }
  };

  const joinUrl = `${window.location.origin}?room=${roomCode}`;

  const copyToClipboard = async () => {
    if (!roomCode) return;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.open(`/?room=${roomCode}`, "_blank");
    }
  };

  const handleContinueToGame = () => {
    navigate("/");
  };

  const handleSpectate = () => {
    if (roomCode) {
      window.open(`/spectate?room=${roomCode}`, "_blank");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {!showQR ? (
          <div className="w-full max-w-md">
            {/* Logo en dehors de la carte blanche */}
            <div className="flex justify-center -mt-12 mb-2">
              <img src="/assets/logo.svg" alt="Logo" className="w-64 h-64" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6 bg-white rounded-2xl shadow-xl p-8"
            >
              <h1 className="text-3xl font-bold text-purple-800 mb-6">
                Create a New Room
              </h1>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter your nickname
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Your nickname"
                />
              </div>

              <button
                onClick={handleCreateRoom}
                disabled={!name.trim() || loading}
                className="w-full py-3 px-6 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Creating Room..." : "Create Room"}
              </button>
            </motion.div>
          </div>
        ) : (
          <div className="w-full max-w-md">
            {/* Logo placé à l'extérieur de la carte blanche au-dessus du QR */}
            <div className="flex justify-center -mt-12 mb-4">
              <img src="/assets/logo.svg" alt="Logo" className="w-24 h-24" />
            </div>
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center bg-white rounded-2xl shadow-xl p-8"
              >
                <h2 className="text-2xl font-bold text-purple-800 mb-4">
                  Room Created!
                </h2>

                <div className="bg-purple-50 p-4 rounded-lg mb-6">
                  <p className="text-sm text-purple-600 mb-2">Room Code</p>
                  <p className="text-3xl font-bold text-purple-800">
                    {roomCode}
                  </p>
                </div>

                <div className="flex justify-center mb-6">
                  <div className="p-4 bg-white rounded-xl shadow-md">
                    <QRCodeSVG
                      value={joinUrl}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                </div>

                <div className="flex gap-2 mb-8">
                  <button
                    onClick={copyToClipboard}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    {copied ? "Link copied" : "Copy invite link"}
                  </button>
                  <button
                    onClick={handleSpectate}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Spectate
                  </button>
                </div>

                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-purple-800 flex items-center">
                      <Users className="w-5 h-5 mr-2" />
                      Players in Lobby
                    </h3>
                    <span className="text-purple-600">
                      {players.length}{" "}
                      {players.length === 1 ? "player" : "players"}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {players.map((player) => (
                      <motion.div
                        key={player.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-purple-50 p-3 rounded-lg"
                      >
                        <span className="font-medium text-purple-700">
                          {player.name}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleContinueToGame}
                  className="w-full py-3 px-6 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Continue to Game
                </motion.button>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
